/**
 * Shared component for every admin/owner page: role-based sidebar navigation, navbar
 * with profile menu, and logout confirmation. Page content is passed in as children.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  Bars3Icon,
  HomeIcon,
  EnvelopeIcon,
  ClockIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarLeftExpand } from 'react-icons/tb';
import logo from '../assets/Grey Imports Logo.png';
import Footer from './Footer';
import PageTransition from './PageTransition';
import { useAuth, supabase } from '../context/AuthContext';
import useSidebarNavScroll from '../hooks/useSidebarNavScroll';

const ADMIN_SIDEBAR_PINNED_STORAGE_KEY = 'admin-sidebar-pinned';

const adminFeatureLinks = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: HomeIcon,
    type: 'link',
    to: '/admin',
  },
  {
    id: 'add-model-report',
    label: 'New Model Report',
    icon: PlusCircleIcon,
    type: 'link',
    to: '/add-new-customer-model-reports',
  },
  {
    id: 'model-report-list',
    label: 'Model Report List',
    icon: DocumentTextIcon,
    type: 'group',
    children: [
      {
        id: 'model-report-list',
        label: 'Model Report List',
        to: '/admin/model-reports',
        state: { from: '/admin' },
      },
      {
        id: 'variation-history',
        label: 'Variation History',
        to: '/admin/variation-history',
      },
    ],
  },
  {
    id: 'price-management',
    label: 'Price Management',
    icon: CurrencyDollarIcon,
    type: 'link',
    to: '/admin/price-management',
  },
  {
    id: 'sevs-expiry',
    label: 'SEVS Expiry',
    icon: ClockIcon,
    type: 'group',
    children: [
      {
        id: 'sevs-expiry',
        label: 'SEVS Expiry Management',
        to: '/sevs-expiry',
      },
      {
        id: 'archived-sevs',
        label: 'Archived SEVS',
        to: '/archived-sevs',
      },
    ],
  },
  {
    id: 'verify-uid',
    label: 'U.ID Verification ',
    icon: ShieldCheckIcon,
    type: 'button',
  },
  {
    id: 'archived-orders',
    label: 'Archived Orders',
    icon: ArchiveBoxIcon,
    type: 'link',
    to: '/archived-orders',
  },
  {
    id: 'send-email',
    label: 'Send Marketing Email',
    icon: EnvelopeIcon,
    type: 'link',
    to: '/send-marketing-email',
  },
  {
    id: 'view-analysis',
    label: 'Analytics',
    icon: ChartBarIcon,
    type: 'group',
    children: [
      {
        id: 'order-analytics',
        label: 'Order Analytics',
        to: '/view-analysis',
        state: { analysisView: 'orders' },
      },
      {
        id: 'revenue-analytics',
        label: 'Revenue Analytics',
        to: '/view-analysis',
        state: { analysisView: 'revenue' },
      },
      {
        id: 'performance-overview',
        label: 'Performance Overview',
        to: '/view-analysis',
        state: { analysisView: 'performance' },
      },
    ],
    // Analytics reveals revenue figures, so it is owner-only.
    roles: ['owner'],
  },
];

const AdminLayout = ({
  activeItemId,
  title,
  subtitle,
  children,
  contentClassName = 'mx-auto w-full max-w-4xl',
  footerContainerClassName = 'mt-10 lg:mt-14',
  showProfileMenu = true,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(false);
  // Pinned state survives reloads, so the sidebar stays wide until the user unpins it.
  const [isDesktopSidebarPinned, setIsDesktopSidebarPinned] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem(ADMIN_SIDEBAR_PINNED_STORAGE_KEY) === 'true';
  });
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [profile, setProfile] = useState({ name: 'Admin', email: '', phone: '' });
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, signOut, isOwner, userRole } = useAuth();
  // Customers can land on an admin route; hide the sidebar entirely rather than leaking nav links.
  const canShowAdminSidebar = userRole === 'admin' || userRole === 'owner';
  // Desktop sidebar widens either on hover or while pinned; mobile uses a separate slide-in drawer.
  const isDesktopSidebarVisible = canShowAdminSidebar && (isDesktopSidebarExpanded || isDesktopSidebarPinned);
  // The open mobile drawer sits over the header, so header controls get dimmed and disabled.
  const isMobileSidebarBlockingHeader = canShowAdminSidebar && isSidebarOpen;

  const desktopNavRef = useSidebarNavScroll(
    'admin-desktop-nav',
    activeItemId,
    `${expandedGroupId ?? ''}:${isDesktopSidebarVisible ? '1' : '0'}`
  );

  // Close the profile dropdown on any click happening outside it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ADMIN_SIDEBAR_PINNED_STORAGE_KEY, String(isDesktopSidebarPinned));
  }, [isDesktopSidebarPinned]);

  // Load the display name/contact details for the profile menu from the users table.
  useEffect(() => {
    const fetchUserDetails = async () => {
      // No session row yet: fall back to whatever the auth user carries.
      if (!user?.id) {
        setProfile({
          name: 'Admin',
          email: user?.email || '',
          phone: user?.phone || '',
        });
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('name, email, phone')
          .eq('id', user.id)
          .limit(1)
          .maybeSingle();

        if (error) {
          throw error;
        }

        setProfile({
          name: data?.name || 'Admin',
          email: data?.email || user?.email || '',
          phone: data?.phone || user?.phone || '',
        });
      } catch (error) {
        console.error('Error fetching user details:', error);
        setProfile({
          name: 'Admin',
          email: user?.email || '',
          phone: user?.phone || '',
        });
      }
    };

    fetchUserDetails();
  }, [user]);

  // Drop role-restricted entries; a plain admin is treated as the 'admin' role, never 'owner'.
  const visibleLinks = useMemo(
    () => adminFeatureLinks.filter((item) => !item.roles || item.roles.includes(isOwner ? 'owner' : 'admin')),
    [isOwner]
  );

  useEffect(() => {
    const activeGroup = visibleLinks.find(
      (item) => item.type === 'group' && item.children?.some((child) => child.id === activeItemId)
    );

    if (activeGroup) {
      setExpandedGroupId(activeGroup.id);
    }
  }, [activeItemId, visibleLinks]);

  // When the user clicks the "Verify VIN" button, navigate to the VIN verification screen.
  // The VIN screen can send the admin back to the dashboard.
  const handleVerifyVin = () => {
    navigate('/verify-vin', { state: { from: '/admin' } });
  };

  const handleLogoClick = () => {
    window.location.reload();
  };

  const handleSidebarAction = (featureId) => {
    if (featureId === 'verify-uid') {
      setExpandedGroupId(null);
      handleVerifyVin();
      setIsSidebarOpen(false);
    }
  };

  const handleNavClick = () => {
    setExpandedGroupId(null);
    setIsSidebarOpen(false);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirmation(false);
    try {
      await signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const SidebarNavItem = ({ item, compact = false }) => {
    const Icon = item.icon;
    const isGroup = item.type === 'group';
    const isGroupActive = isGroup && item.children?.some((child) => child.id === activeItemId);
    const isExpanded = isGroup && expandedGroupId === item.id;
    // Only one solid-blue selection at a time: expanded group wins over route active item
    const isSelected = expandedGroupId
      ? isExpanded
      : item.id === activeItemId || isGroupActive;
    const showExpandedDesktopContent = compact && isDesktopSidebarVisible;

    const mobileBaseClassName = `group relative flex min-h-[4.5rem] w-full items-center gap-4 rounded-[1.75rem] px-5 py-3 text-[13px] font-medium transition-all duration-300 ease-out xl:text-sm ${isSelected ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'}`;

    const baseClassName = compact
      ? `group relative flex h-10 items-center overflow-hidden rounded-2xl border text-[13px] font-medium transition-all duration-300 ease-out xl:text-sm ${showExpandedDesktopContent ? 'w-full justify-start gap-2.5 px-3.5' : 'w-10 justify-center'} ${isSelected ? 'border-blue-200 bg-blue-600 text-white' : 'border-slate-200 bg-white text-blue-600 hover:border-blue-200 hover:bg-blue-50'}`
      : mobileBaseClassName;

    const labelRailClassName = 'pointer-events-none absolute left-full top-1/2 z-50 ml-3 hidden max-w-[12rem] -translate-y-1/2 whitespace-normal rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 opacity-0 shadow-xl shadow-slate-200/70 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 lg:block';

    const content = (
      <>
        <span className={compact ? `flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${showExpandedDesktopContent ? (isSelected ? 'bg-transparent text-white' : 'bg-transparent text-blue-600') : ''}` : `flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-colors ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-blue-600 group-hover:bg-blue-100'}`}>
          <Icon className="h-5 w-5" />
        </span>
        {(!compact || showExpandedDesktopContent) && (
          <span className="min-w-0">
            <span className="block truncate">{item.label}</span>
          </span>
        )}
      </>
    );

    if (isGroup) {
      if (compact) {
        const railClassName = showExpandedDesktopContent
          ? `grid overflow-hidden transition-all duration-300 ${isExpanded ? 'mt-2 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`
          : `absolute left-full top-1/2 z-50 ml-3 w-56 -translate-y-1/2 rounded-2xl border border-slate-200 bg-white/95 p-2 text-slate-900 opacity-0 shadow-xl shadow-slate-200/70 backdrop-blur-md transition-all duration-300 ease-out pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100`;

        return (
          <div className={`group relative ${showExpandedDesktopContent ? 'w-full' : 'overflow-visible'}`}>
            <button
              type="button"
              className={baseClassName}
              onClick={() => setExpandedGroupId(isExpanded ? null : item.id)}
              aria-haspopup="menu"
              aria-expanded={isExpanded}
              data-sidebar-active={!showExpandedDesktopContent && isGroupActive ? 'true' : undefined}
            >
              {content}
              {showExpandedDesktopContent ? (
                <span
                  className={`ml-auto flex items-center justify-center ${isSelected ? 'text-white' : 'text-blue-600'}`}
                >
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </span>
              ) : (
                <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-slate-900 opacity-0 shadow-xl shadow-slate-200/70 transition-all duration-300 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 xl:text-sm">
                  {item.label}
                </span>
              )}
            </button>
            <div className={railClassName}>
              <div className={showExpandedDesktopContent ? 'min-h-0' : ''}>
                {!showExpandedDesktopContent && (
                  <div className="mb-2 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-[13px] font-semibold text-slate-900 xl:text-sm">{item.label}</span>
                    <ChevronRightIcon className="h-4 w-4 text-slate-400" />
                  </div>
                )}
                <div className={showExpandedDesktopContent ? 'ml-4 space-y-1 border-l border-slate-200 pl-4' : 'space-y-1'}>
                  {item.children.map((child) => {
                    const childActive = child.id === activeItemId;

                    return (
                      <Link
                        key={child.id}
                        to={child.to}
                        state={child.state}
                        data-sidebar-active={childActive ? 'true' : undefined}
                        className={`flex items-center rounded-xl px-3 py-2 text-[13px] font-medium transition xl:text-sm ${childActive ? (showExpandedDesktopContent ? 'bg-blue-50 text-blue-700' : 'bg-blue-600 text-white shadow-sm') : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
                      >
                        {child.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="rounded-2xl">
          <button
            type="button"
            className={baseClassName}
            onClick={() => setExpandedGroupId(isExpanded ? null : item.id)}
            aria-expanded={isExpanded}
          >
            {content}
            <span
              className={`ml-auto flex items-center justify-center ${isSelected ? 'text-white' : 'text-blue-600'}`}
            >
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </span>
          </button>
          <div className={`grid overflow-hidden transition-all duration-300 ${isExpanded ? 'mt-2 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
            <div className="min-h-0">
              <div className="ml-[1.75rem] space-y-1 border-l border-slate-200 pl-6">
                {item.children.map((child) => {
                  const childActive = child.id === activeItemId;

                    return (
                      <Link
                        key={child.id}
                        to={child.to}
                        state={child.state}
                        onClick={() => setIsSidebarOpen(false)}
                        data-sidebar-active={childActive ? 'true' : undefined}
                        className={`flex min-h-[3rem] items-center rounded-xl px-4 py-2 text-[13px] font-medium transition xl:text-sm ${childActive ? 'bg-blue-50 text-blue-700' : 'text-slate-800 hover:bg-slate-100 hover:text-blue-700'}`}
                      >
                        {child.label}
                      </Link>
                    );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (item.type === 'link') {
      return (
        <Link
          to={item.to}
          state={item.state}
          className={baseClassName}
          onClick={handleNavClick}
          data-sidebar-active={item.id === activeItemId ? 'true' : undefined}
        >
          {content}
          {compact && !showExpandedDesktopContent && <span className={labelRailClassName}>{item.label}</span>}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handleSidebarAction(item.id)}
        className={baseClassName}
        data-sidebar-active={item.id === activeItemId ? 'true' : undefined}
      >
        {content}
        {compact && !showExpandedDesktopContent && <span className={labelRailClassName}>{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      {/* Tap-anywhere that closes the mobile drawer */}
      {canShowAdminSidebar && isSidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          className="fixed inset-0 z-30 bg-slate-950/45 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Desktop sidebar: widens on hover, but stays put once pinned */}
      {canShowAdminSidebar && (
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden overflow-hidden border-r border-slate-200 bg-white/85 px-3 py-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur-md transition-[width] duration-300 ease-out lg:flex lg:flex-col xl:px-4 xl:py-6 ${isDesktopSidebarVisible ? 'w-[16.5rem] xl:w-[17.5rem]' : 'w-20 lg:items-center xl:w-24'}`}
        onMouseEnter={() => setIsDesktopSidebarExpanded(true)}
        onMouseLeave={() => {
          if (!isDesktopSidebarPinned) {
            setIsDesktopSidebarExpanded(false);
          }
        }}
      >
        <div className={`flex min-h-0 flex-1 flex-col gap-3 ${isDesktopSidebarVisible ? 'pr-1' : 'items-center overflow-visible'}`}>
          <div className={`flex ${isDesktopSidebarVisible ? 'items-center gap-2.5' : 'flex-col items-center gap-2'}`}>
              <button
                type="button"
                onClick={handleLogoClick}
                className="flex h-14 w-14 cursor-pointer items-center justify-center sm:h-16 sm:w-16 xl:h-18 xl:w-18"
                aria-label="Reload page"
              >
                <img src={logo} alt="Grey Imports" className="h-12 w-12 object-contain sm:h-14 sm:w-14 xl:h-16 xl:w-16" />
              </button>
              {isDesktopSidebarVisible && (
                <div>

                  <h2 className="text-sm font-semibold text-slate-900 xl:text-base uppercase tracking-[0.04em]">Admin Portal</h2>
                </div>
              )}
          </div>
          <div className={`h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent ${isDesktopSidebarVisible ? 'w-full' : 'w-20'}`} />
          <nav
            ref={desktopNavRef}
            className={`admin-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${isDesktopSidebarVisible ? 'pr-1' : ''}`}
          >
            <div className={`flex flex-col gap-2.5 ${isDesktopSidebarVisible ? 'items-stretch' : 'items-center'}`}>
            {visibleLinks.map((item) => (
              <SidebarNavItem key={item.id} item={item} compact />
            ))}
            </div>
          </nav>
          <div className={`shrink-0 pt-2.5 ${isDesktopSidebarVisible ? 'border-t border-slate-200/80' : ''}`}>
            <button
              type="button"
              onClick={() => {
                setIsDesktopSidebarPinned((current) => {
                  const nextPinned = !current;

                  // Unpinning must also collapse now, otherwise the sidebar stays wide until mouse-out.
                  if (!nextPinned) {
                    setIsDesktopSidebarExpanded(false);
                  }

                  return nextPinned;
                });
              }}
              className={`group relative flex h-12 items-center overflow-hidden rounded-2xl border transition-all duration-300 ease-out ${isDesktopSidebarVisible ? 'w-full px-2.5 py-2' : 'w-12 justify-center'} ${isDesktopSidebarPinned ? 'border-blue-200 bg-blue-600 text-white shadow-lg shadow-blue-200/60' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600'}`}
              aria-label={isDesktopSidebarPinned ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-pressed={isDesktopSidebarPinned}
              title={isDesktopSidebarPinned ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-300 ${isDesktopSidebarPinned ? 'bg-white/15 text-white' : 'text-current'}`}>
                {isDesktopSidebarPinned ? (
                  <TbLayoutSidebarLeftCollapse className="h-4 w-4 xl:h-5 xl:w-5" />
                ) : (
                  <TbLayoutSidebarLeftExpand className="h-4 w-4 xl:h-5 xl:w-5" />
                )}
              </span>
              <span
                className={`min-w-0 flex-1 overflow-hidden text-left transition-all duration-300 ease-out ${isDesktopSidebarVisible ? 'ml-2.5 max-w-[10rem] opacity-100' : 'ml-0 max-w-0 opacity-0'}`}
                aria-hidden={!isDesktopSidebarVisible}
              >
                <span className="block min-w-0">
                  <span
                    className={`block text-xs font-semibold transition-all duration-200 ease-out xl:text-sm ${isDesktopSidebarVisible ? 'translate-x-0 opacity-100 delay-100' : 'translate-x-2 opacity-0 delay-0'}`}
                  >
                    {isDesktopSidebarPinned ? 'Collapse Sidebar' : 'Expand Sidebar'}
                  </span>
                  <span
                    className={`block text-[10px] transition-all duration-200 ease-out xl:text-xs ${isDesktopSidebarPinned ? 'text-blue-100' : 'text-slate-400 group-hover:text-blue-500'} ${isDesktopSidebarVisible ? 'translate-x-0 opacity-100 delay-150' : 'translate-x-2 opacity-0 delay-0'}`}
                  >
                    {isDesktopSidebarPinned ? 'Click to return to hover mode' : 'Pin the sidebar in place'}
                  </span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </aside>
      )}

      {/* Mobile drawer: slides in from the left, toggled by the header hamburger */}
      {canShowAdminSidebar && (
      <aside className={`fixed inset-y-0 left-0 z-40 w-[18rem] max-w-[88vw] transform overflow-y-auto border-r border-slate-200 bg-white px-4 py-5 text-slate-900 shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition-transform duration-300 sm:w-[18.5rem] sm:px-5 sm:py-6 lg:hidden ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex min-h-full flex-col">
          <div className="mb-6 flex items-center justify-between gap-3 sm:mb-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleLogoClick}
                className="flex h-16 w-16 cursor-pointer items-center justify-center sm:h-20 sm:w-20"
                aria-label="Reload page"
              >
                <img src={logo} alt="Grey Imports" className="h-14 w-14 object-contain sm:h-16 sm:w-16" />
              </button>
              <div>
                <h2 className="text-base font-semibold text-slate-900 sm:text-md uppercase tracking-[0.02em]">Admin Portal</h2>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsSidebarOpen(false)}
              className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-5 h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          <nav className="admin-scrollbar flex-1 space-y-2 overflow-y-auto pr-1">
            {visibleLinks.map((item) => (
              <SidebarNavItem key={item.id} item={item} />
            ))}
          </nav>
        </div>
      </aside>
      )}

      <div className={`transition-[padding] duration-300 ease-out ${canShowAdminSidebar ? (isDesktopSidebarVisible ? 'lg:pl-[18.5rem]' : 'lg:pl-24') : ''}`}>
        <div className="mx-auto w-full max-w-[1800px] px-3 py-3 sm:px-4 sm:py-4 md:px-6 lg:px-8 lg:py-6">
          <PageTransition>
            <div className="mb-5 rounded-[1.5rem] border border-slate-200 bg-gradient-to-r from-white via-slate-50 to-slate-100 p-4 text-slate-900 shadow-sm sm:mb-6 sm:p-5 lg:rounded-[2rem] lg:p-6">
              <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-6 xl:gap-8">
                <div className="contents sm:flex sm:min-w-0 sm:items-start sm:gap-4">
                  {canShowAdminSidebar && (
                    <button
                      type="button"
                      onClick={() => setIsSidebarOpen(true)}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 sm:mt-1 sm:h-11 sm:w-11 lg:hidden"
                    >
                      <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  )}
                  <div className="min-w-0 flex-1 self-center sm:self-auto">
                    <h1 className="text-lg font-medium uppercase tracking-[0.06em] sm:mt-2 sm:text-xl sm:tracking-[0.08em] lg:text-2xl 2xl:text-[2rem]">
                      {title}
                    </h1>
                    <p className="mt-2 hidden max-w-4xl text-sm leading-6 text-slate-600 sm:block sm:text-base sm:leading-7">
                      {subtitle}
                    </p>
                  </div>
                </div>

                <div className={`relative z-30 flex min-w-0 justify-self-end self-center sm:self-start ${isMobileSidebarBlockingHeader ? 'pointer-events-none blur-[1.5px] opacity-60 lg:pointer-events-auto lg:blur-0 lg:opacity-100' : ''}`}>
                  {showProfileMenu && (
                    <div className="relative z-50" ref={dropdownRef}>
                      <button
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        disabled={isMobileSidebarBlockingHeader}
                        className={`ml-auto flex w-auto min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left transition hover:bg-slate-50 sm:gap-3 xl:min-w-[220px] xl:px-4 ${isMobileSidebarBlockingHeader ? 'cursor-not-allowed' : ''} lg:cursor-pointer`}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 sm:h-11 sm:w-11">
                          <UserCircleIcon className="h-6 w-6 text-slate-700 sm:h-7 sm:w-7" />
                        </span>
                        <span className="hidden min-w-0 flex-1 xl:block">
                          <span className="block truncate text-sm font-semibold text-slate-900">{profile.name}</span>
                        </span>
                        <ChevronDownIcon className={`h-5 w-5 text-slate-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isProfileOpen && !isMobileSidebarBlockingHeader && (
                        <div className="absolute right-0 z-50 mt-3 w-full min-w-[15rem] rounded-2xl border border-slate-200 bg-white p-2 text-slate-900 shadow-2xl sm:min-w-[16rem] sm:w-auto">
                          <div className="rounded-xl bg-slate-50 px-4 py-3">
                            <p className="text-sm font-semibold text-slate-900">{profile.name}</p>
                            <p className="mt-1 text-sm text-slate-500">{profile.email}</p>
                            <p className="text-sm text-slate-500">{profile.phone ? `+${profile.phone}` : ''}</p>
                          </div>
                          <hr className="my-2 border-slate-200" />
                          <button
                            onClick={() => setShowLogoutConfirmation(true)}
                            className="mt-2 flex w-full items-center gap-2 rounded-xl px-4 py-1 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700"
                          >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`pt-2 sm:pt-3 ${contentClassName}`}>{children}</div>
          </PageTransition>
        </div>
      </div>

      <div className={footerContainerClassName}>
        <Footer portal="admin" />
      </div>

      {showLogoutConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
          <div className="mx-4 w-full max-w-sm rounded-lg border border-slate-300 bg-white p-6 ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]">
            <div className="mb-4 flex items-center">
              <div className="flex-shrink-0">
                <ArrowRightOnRectangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Are You Sure?</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to logout? You will need to sign in again to access your account.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLogoutConfirmation(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { adminFeatureLinks };
export default AdminLayout;